import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import React__default, { PureComponent } from 'react';
import PT from 'prop-types';
import styled, { css } from 'styled-components';
import { i as isMobile } from '../theme/style-mixins/isMobile/isMobile-CGsYoba7.js';

const NodeSafeHTMLElement = typeof HTMLElement !== 'undefined' ? HTMLElement : Object;
const fontSizeRuleMixin = /*#__PURE__*/css(["font-size:", ";", ""], ({
  theme,
  fontSize
}) => theme.typography.size[fontSize], isMobile(css(["font-size:", ";"], ({
  theme,
  fontSize
}) => theme.typography.mobile.size[fontSize])));
const colorRule = ({
  theme,
  textColor
}) => {
  if (!textColor) return 'color: inherit;';
  return `color: ${theme.typography.color[textColor] || 'inherit'};`;
};
const inlineRule = ({
  inline
}) => inline && 'display:inline;';
const withMarginRule = marginId => ({
  theme: {
    typography
  },
  withMargin
}) => `margin: ${withMargin ? typography[marginId] : 0};`;
const styleAsRule = defaultStyleId => ({
  styleAs,
  theme
}) => theme.typography[styleAs || defaultStyleId];
const styleAsMobileRule = defaultStyleId => ({
  styleAs,
  theme
}) => theme.typography.mobile[styleAs || defaultStyleId];
const CodeStyleRule = /*#__PURE__*/css(["", ";color:", ";font-family:monospace;padding:0 4px;border-radius:3px;"], ({
  theme
}) => theme.typography.code, ({
  theme
}) => theme.typography.color.base);
const StyledH1 = /*#__PURE__*/styled.h1.withConfig({
  componentId: "sc-storm-ui-30074613__sc-b4z2xs-0"
})(["", " ", " ", " ", ""], styleAsRule('h1'), isMobile(css(["", ""], styleAsMobileRule('h1'))), colorRule, withMarginRule('h1Margin'));
StyledH1.displayName = 'StyledH1';
const StyledH2 = /*#__PURE__*/styled.h2.withConfig({
  componentId: "sc-storm-ui-30074613__sc-b4z2xs-1"
})(["", " ", " ", " ", " ", ""], styleAsRule('h2'), isMobile(css(["", ""], styleAsMobileRule('h2'))), inlineRule, colorRule, withMarginRule('h2Margin'));
StyledH2.displayName = 'StyledH2';
const StyledH3 = /*#__PURE__*/styled.h3.withConfig({
  componentId: "sc-storm-ui-30074613__sc-b4z2xs-2"
})(["", " ", " ", " ", " ", ""], styleAsRule('h3'), isMobile(css(["", ""], styleAsMobileRule('h3'))), inlineRule, colorRule, withMarginRule('h3Margin'));
StyledH3.displayName = 'StyledH3';
const StyledH4 = /*#__PURE__*/styled.h4.withConfig({
  componentId: "sc-storm-ui-30074613__sc-b4z2xs-3"
})(["", " ", " ", " ", " ", ""], styleAsRule('h4'), isMobile(css(["", ""], styleAsMobileRule('h4'))), inlineRule, colorRule, withMarginRule('h4Margin'));
StyledH4.displayName = 'StyledH4';
const StyledH5 = /*#__PURE__*/styled.h5.withConfig({
  componentId: "sc-storm-ui-30074613__sc-b4z2xs-4"
})(["", " ", " ", " ", " ", ""], styleAsRule('h5'), isMobile(css(["", ""], styleAsMobileRule('h5'))), inlineRule, colorRule, withMarginRule('h5Margin'));
StyledH5.displayName = 'StyledH5';
const StyledH6 = /*#__PURE__*/styled.h6.withConfig({
  componentId: "sc-storm-ui-30074613__sc-b4z2xs-5"
})(["", " ", " ", " ", " ", ""], styleAsRule('h6'), isMobile(css(["", ""], styleAsMobileRule('h6'))), inlineRule, colorRule, withMarginRule('h6Margin'));
StyledH6.displayName = 'StyledH6';
const StyledLead = /*#__PURE__*/styled.p.withConfig({
  componentId: "sc-storm-ui-30074613__sc-b4z2xs-6"
})(["", " ", " ", " ", " ", " :last-child{", "}"], ({
  theme
}) => theme.typography.lead, isMobile(css(["", ""], ({
  theme
}) => theme.typography.mobile.lead)), inlineRule, colorRule, withMarginRule('leadMargin'), withMarginRule('baseMargin'));
StyledLead.displayName = 'StyledLead';
const StyledP = /*#__PURE__*/styled.p.withConfig({
  componentId: "sc-storm-ui-30074613__sc-b4z2xs-7"
})(["", " ", " ", " ", " ", " a{text-decoration:underline;}:last-child{", "}"], ({
  theme
}) => theme.typography.base, isMobile(css(["", ""], ({
  theme
}) => theme.typography.mobile.base)), inlineRule, colorRule, withMarginRule('baseMargin'), withMarginRule('baseMargin'));
StyledP.displayName = 'StyledP';
const StyledSpan = /*#__PURE__*/styled.span.withConfig({
  componentId: "sc-storm-ui-30074613__sc-b4z2xs-8"
})(["", " ", " display:inline;", ""], ({
  theme
}) => theme.typography.base, isMobile(css(["", ""], ({
  theme
}) => theme.typography.mobile.base)), colorRule);
StyledSpan.displayName = 'StyledSpan';
const StyledSup = /*#__PURE__*/styled.sup.withConfig({
  componentId: "sc-storm-ui-30074613__sc-b4z2xs-9"
})(["", " ", " color:", ";:last-child{", "}"], ({
  theme
}) => theme.typography.super, withMarginRule('baseMargin'), ({
  theme
}) => theme.palette.rust, withMarginRule('baseMargin'));
StyledSup.displayName = 'StyledSup';
const StyledScreenReaderOnly = /*#__PURE__*/styled.span.withConfig({
  componentId: "sc-storm-ui-30074613__sc-b4z2xs-10"
})(["", ""], ({
  theme
}) => theme.typography.screenReaderOnly);
StyledScreenReaderOnly.displayName = 'StyledScreenReaderOnly';

/**
 * The `pre` tag is cast to a `span` to avoid prop collisions
 */
const StyledPre = /*#__PURE__*/styled.pre.withConfig({
  componentId: "sc-storm-ui-30074613__sc-b4z2xs-11"
})(["", " ", " ", ""], styleAsRule('code'), isMobile(css(["", ""], styleAsMobileRule('code'))), CodeStyleRule);
StyledPre.displayName = 'StyledPre';
const StyledCode = /*#__PURE__*/styled.code.withConfig({
  componentId: "sc-storm-ui-30074613__sc-b4z2xs-12"
})(["", " ", " ", ""], styleAsRule('code'), isMobile(css(["", ""], styleAsMobileRule('code'))), CodeStyleRule);
StyledCode.displayName = 'StyledCode';
class TextComponent extends PureComponent {
  render() {
    const {
      type,
      fontSize,
      elementRef,
      ...rest
    } = this.props;
    switch (type) {
      case 'h1':
        return /*#__PURE__*/React__default.createElement(StyledH1, _extends({}, rest, {
          ref: elementRef
        }));
      case 'h2':
        return /*#__PURE__*/React__default.createElement(StyledH2, _extends({}, rest, {
          ref: elementRef
        }));
      case 'h3':
        return /*#__PURE__*/React__default.createElement(StyledH3, _extends({}, rest, {
          ref: elementRef
        }));
      case 'h4':
        return /*#__PURE__*/React__default.createElement(StyledH4, _extends({}, rest, {
          ref: elementRef
        }));
      case 'h5':
        return /*#__PURE__*/React__default.createElement(StyledH5, _extends({}, rest, {
          ref: elementRef
        }));
      case 'h6':
        return /*#__PURE__*/React__default.createElement(StyledH6, _extends({}, rest, {
          ref: elementRef
        }));
      case 'lead':
        return /*#__PURE__*/React__default.createElement(StyledLead, _extends({}, rest, {
          ref: elementRef
        }));
      case 'sup':
        return /*#__PURE__*/React__default.createElement(StyledSup, _extends({}, rest, {
          ref: elementRef
        }));
      case 'span':
        return /*#__PURE__*/React__default.createElement(StyledSpan, _extends({}, rest, {
          ref: elementRef
        }));
      case 'sr-only':
        return /*#__PURE__*/React__default.createElement(StyledScreenReaderOnly, _extends({}, rest, {
          ref: elementRef
        }));
      case 'pre':
        return /*#__PURE__*/React__default.createElement(StyledPre, _extends({}, rest, {
          ref: elementRef
        }));
      case 'code':
        return /*#__PURE__*/React__default.createElement(StyledCode, _extends({}, rest, {
          ref: elementRef
        }));
      default:
        return /*#__PURE__*/React__default.createElement(StyledP, _extends({}, rest, {
          ref: elementRef
        }));
    }
  }
}
_defineProperty(TextComponent, "propTypes", {
  type: PT.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'lead', 'sup', 'span', 'sr-only', 'pre', 'code']),
  withMargin: PT.bool,
  styleAs: PT.string,
  inline: PT.bool,
  textColor: PT.oneOf(['base', 'secondary', 'tertiary']),
  fontSize: PT.oneOf(['extraLarge', 'large', 'medium', 'base', 'small', 'mini', 'tiny']),
  className: PT.string.isRequired,
  elementRef: PT.shape({
    current: PT.instanceOf(NodeSafeHTMLElement)
  })
});
_defineProperty(TextComponent, "defaultProps", {
  type: 'p',
  withMargin: false,
  styleAs: undefined,
  inline: false,
  textColor: null,
  fontSize: undefined,
  elementRef: undefined
});
const Text = /*#__PURE__*/styled(TextComponent).withConfig({
  componentId: "sc-storm-ui-30074613__sc-b4z2xs-13"
})(["", ""], fontSizeRuleMixin);
Text.displayName = 'Text';
Text.propTypes = {
  /**
   * Is one of h1, h2, h3, h4, h5, h6, lead, p, span
   * @defaultValue 'p'
   */
  type: PT.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'lead', 'sup', 'span', 'sr-only', 'pre', 'code']),
  /**
   * Adds the "User Agent" margins. Useful when doing standard article style text placement.
   * @defaultValue false
   */
  withMargin: PT.bool,
  /**
   * Is one of h1, h2, h3, h4, h5, h6. This allows you to use the correct semantic tag,
   * but style it as something different (e.g, if something is techically an <h2>,
   * but you want it to look like an <h6>).
   * @defaultValue undefined
   */
  styleAs: PT.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  /**
   * @defaultValue false
   */
  inline: PT.bool,
  /**
   * Pre-defined text color styles. Is one of primary, secondary, or tertiary.
   * @defaultValue null
   */
  textColor: PT.oneOf(['base', 'secondary', 'tertiary']),
  /**
   * The size of the text element.
   * @defaultValue undefined
   */
  fontSize: PT.oneOf(['extraLarge', 'large', 'medium', 'base', 'small', 'mini', 'tiny']),
  className: PT.string,
  /**
  * A React Ref that contains the underlying HTMLParagraphElement this component will render.
  * @defaultValue undefined
  */
  elementRef: PT.shape({
    current: PT.instanceOf(NodeSafeHTMLElement)
  })
};

export { Text as T, TextComponent as a };
//# sourceMappingURL=Text-DZ4qSn7m.js.map
